import { Autocomplete, Button, CircularProgress, TextField } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import InputField from "components/Input/InputField";
import TextArea from "components/Input/TextArea";
import ComposeEmailDrawer from "components/Modals/ComposeEmailDrawer";
import useKothar from "context/useKothar";
import { format, isValid } from "date-fns";
import { formatDisplayDate } from "utils/dateFormat";
import { useCallback, useEffect, useState } from "react";
import {
  FaArrowLeft,
  FaBan,
  FaCalendarAlt,
  FaCheckCircle,
  FaComments,
  FaEnvelope,
  FaListAlt,
  FaMapMarkerAlt,
  FaPassport,
  FaPhone,
  FaSave,
  FaTimes,
  FaUser,
} from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

// Inline promo modal variants for quick preview

const safeFormat = (dateStr, fmt) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return isValid(d) ? format(d, fmt) : null;
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const ViewCheckIn = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const item = location.state?.item;
  const [{}, { refetchCheckInData }] = useKothar();
  const [openEmailModal, setOpenEmailModal] = useState(false);
  const [formData, setFormData] = useState({
    clientConversionStatus: false,
    followUpDate: "",
    summary: "",
    discussion: "",
    notes: "",
  });
  const { id } = useParams();

  const [isEditing, setIsEditing] = useState(false);

  const getCheckInData = useCallback(async (id) => {
    try {
      const response = await axios.get(`/client-form/${id}`);
      if (response.data?.data) {
        setFormData(response.data?.data);
        return response.data?.data; // Return the data for React Query
      } else {
        toast.error("No check-in data found");
        return null;
      }
    } catch (error) {
      toast.error("Failed to fetch check-in data");
      return null;
    }
  }, []);

  const {
    data: checkInData,
    isFetching: isCheckInLoading,
    refetch,
  } = useQuery({
    queryKey: ["checkInData", id],
    queryFn: () => getCheckInData(id),
    enabled: !!id,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: true,
  });

  // React Query will automatically fetch data when id changes
  // No need for manual refetch

  const { mutate: updateCheckIn, isLoading } = useMutation({
    mutationFn: (data) => {
      return axios.put(`/client-form/${item?.id}`, data);
    },
    onSuccess: () => {
      toast.success("Check-in data updated successfully");
      setIsEditing(false);
      refetchCheckInData();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to update check-in data");
    },
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = () => {
    // Parse existing custom fields to re-send them as top-level keys
    let extraFields = {};
    try {
      if (formData.extraFieldsJson) {
        const parsed = JSON.parse(formData.extraFieldsJson);
        const metadataKeys = new Set([
          "id",
          "createdDate",
          "updatedDate",
          "convertedClientId",
          "extraFieldsJson",
        ]);
        Object.entries(parsed).forEach(([k, v]) => {
          if (!metadataKeys.has(k)) extraFields[k] = v;
        });
      }
    } catch {}

    const payload = {
      name: formData.name,
      email: formData.email,
      number: formData.number,
      address: formData.address,
      nationality: formData.nationality,
      visaStatus: formData.visaStatus,
      visaExpiry: formData.visaExpiry,
      source: formData.source,
      comments: formData.comments,
      interestedServices: formData.interestedServices,
      gender: formData.gender,
      dob: formData.dob,
      maritalStatus: formData.maritalStatus,
      passportNumber: formData.passportNumber,
      dateOfIssue: formData.dateOfIssue,
      passportExpiry: formData.passportExpiry,
      usiNumber: formData.usiNumber,
      emergencyContactName: formData.emergencyContactName,
      emergencyContact: formData.emergencyContact,
      emergencyRelation: formData.emergencyRelation,
      emergencyEmail: formData.emergencyEmail,
      australianAddress: formData.australianAddress,
      homeCountryAddress: formData.homeCountryAddress,
      currentCourseProvider: formData.currentCourseProvider,
      intake: formData.intake,
      type: formData.type,
      clientConversionStatus:
        formData.clientConversionStatus === "true" ? true : !!formData.clientConversionStatus,
      followUpDate: formData.followUpDate,
      notes: formData.notes,
      summary: formData.summary,
      discussion: formData.discussion,
      ...extraFields,
    };

    updateCheckIn(payload);
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (item) {
      setFormData({
        ...formData,
        discussion: item.discussion || "",
        followUpDate: item.followUpDate || "",
        summaryOfMeeting: item.summaryOfMeeting || "",
        status: item.status || "pending",
        notes: item.notes || "",
      });
    }
  };

  const getStatusConfig = (status) => {
    if (status === true || status === "true") {
      return {
        label: "Converted",
        color: "bg-green-100 text-green-800 border-green-200",
        icon: <FaCheckCircle className="text-green-600" />,
      };
    } else {
      return {
        label: "Not Converted",
        color: "bg-red-100 text-red-800 border-red-200",
        icon: <FaBan className="text-red-600" />,
      };
    }
  };

  const buildMessage = () => {
    const discussionDate = formatDisplayDate(formData.createdDate, "our recent meeting");
    const clientName = escapeHtml(formData.name || "there");

    return `
      <p>Hello ${clientName},</p>
 <br/>
      <p>
        We are sending you this email to get an update from your side. As per the discussion
        we had on ${discussionDate}, we would like to know if you have any questions or if you
        need any help regarding your application and processing.
      </p>

      <p>
        Please let us know if you have any questions or if you need any help regarding your
        application and processing.
      </p>

      <p>Thank you for your time and cooperation.</p>
      <br/>
      <p>
        Best regards,<br />
        <strong>KOTHAR EDUCATIONAL SERVICES</strong><br />
        <a href="mailto:info@kotharedu.com">info@kotharedu.com</a><br />
        <a href="tel:0450466876">0450 466 876</a> | <a href="tel:0480322403">0480 322 403</a><br />
        <a href="https://kotharedu.com/">https://kotharedu.com/</a><br />
        Office - 273/398-403 Pitt Street, Haymarket Sydney
      </p>
    `.trim();
  };

  if (!item) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-app-surface-alt via-app-surface-muted to-app-surface-muted flex items-center justify-center">
        <div className="text-center bg-app-surface rounded-lg shadow-xl p-8 max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaUser className="text-red-600 text-2xl" />
          </div>
          <h3 className="text-xl font-semibold text-app-text mb-2">No Check-in Data Found</h3>
          <p className="text-app-text-secondary mb-6">
            The check-in data you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-app-primary text-white rounded-xl hover:bg-app-primary transition-colors duration-200 font-medium"
          >
            <FaArrowLeft />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(formData.clientConversionStatus);
  const isPersonalLocked = isEditing && !!formData.convertedClientId;

  return (
    <div className="min-h-screen bg-gradient-to-br from-app-surface-alt via-app-surface-muted to-app-surface-muted py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-app-surface rounded-lg shadow-xl border border-white/20 backdrop-blur-sm mb-8">
          <div className="p-6 border-b border-app-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate(-1)}
                  className="p-2 hover:bg-app-surface-hover rounded-xl transition-colors duration-200"
                >
                  <FaArrowLeft className="text-app-text-secondary text-xl" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-app-text">
                    {formData.type === "client-details"
                      ? "Client Details Submission"
                      : "Check-in Details"}
                  </h1>
                  <p className="text-app-text-secondary text-sm">
                    View and manage form submission information
                  </p>
                </div>
              </div>
              <div className="flex gap-3 flex-wrap items-center">
                {formData.convertedClientId && (
                  <>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-100 text-green-800 text-sm font-medium border border-green-200">
                      <FaCheckCircle className="text-green-600" /> Converted to Client
                    </span>
                    <Button
                      variant="outlined"
                      color="success"
                      onClick={() => navigate(`/admin/client/view/${formData.convertedClientId}`)}
                    >
                      View Client
                    </Button>
                  </>
                )}
                {!isEditing ? (
                  <Button
                    variant="contained"
                    onClick={() => setIsEditing(true)}
                    disabled={isCheckInLoading}
                  >
                    Edit
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      disabled={isLoading}
                      startIcon={<FaSave />}
                    >
                      {isLoading ? "Saving..." : "Save"}
                    </Button>
                    <Button variant="outlined" onClick={handleCancel} startIcon={<FaTimes />}>
                      Cancel
                    </Button>
                  </>
                )}
                <Button
                  variant="contained"
                  onClick={() => {
                    setOpenEmailModal(true);
                  }}
                  startIcon={<FaEnvelope />}
                  className="text-white"
                >
                  Send Email
                </Button>
              </div>
            </div>
          </div>
        </div>

        {isCheckInLoading ? (
          <div className="flex justify-center items-center h-screen">
            <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-gray-900">
              <CircularProgress size={100} />
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {isEditing && formData.convertedClientId && (
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-5 py-4 text-amber-800 text-sm">
                <span className="text-lg leading-none mt-0.5">⚠️</span>
                <span>
                  This form has been converted to a client.{" "}
                  <strong>Client information fields are locked</strong> and can only be edited from
                  the client profile. Only follow-up information can be updated here.
                </span>
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Client Information Card */}
              <div className="bg-app-surface rounded-lg shadow-xl border border-white/20 backdrop-blur-sm overflow-hidden">
                <div className="bg-primary px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-app-surface/20 rounded-xl flex items-center justify-center">
                      <FaUser className="text-white text-lg" />
                    </div>
                    <h3 className="text-xl font-semibold text-white">Client Information</h3>
                  </div>
                </div>
                <div className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-app-text-secondary text-sm">
                        <FaUser />
                        <span className="font-medium">Name</span>
                      </div>
                      {isEditing ? (
                        <InputField
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          disabled={isPersonalLocked}
                        />
                      ) : (
                        <p className="text-app-text font-medium">{formData.name || "N/A"}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-app-text-secondary text-sm">
                        <FaEnvelope />
                        <span className="font-medium">Email</span>
                      </div>
                      {isEditing ? (
                        <InputField
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          type="email"
                          disabled={isPersonalLocked}
                        />
                      ) : (
                        <p className="text-app-text font-medium">{formData.email || "N/A"}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-app-text-secondary text-sm">
                        <FaPhone />
                        <span className="font-medium">Phone</span>
                      </div>
                      {isEditing ? (
                        <InputField
                          name="number"
                          value={formData.number}
                          onChange={handleInputChange}
                          type="number"
                          disabled={isPersonalLocked}
                        />
                      ) : (
                        <p className="text-app-text font-medium">{formData.number || "N/A"}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-app-text-secondary text-sm">
                        <FaMapMarkerAlt />
                        <span className="font-medium">Address</span>
                      </div>
                      {isEditing ? (
                        <InputField
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          disabled={isPersonalLocked}
                        />
                      ) : (
                        <p className="text-app-text font-medium">{formData.address || "N/A"}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-app-text-secondary text-sm">
                        <FaPassport />
                        <span className="font-medium">Nationality</span>
                      </div>
                      {isEditing ? (
                        <InputField
                          name="nationality"
                          value={formData.nationality}
                          onChange={handleInputChange}
                          disabled={isPersonalLocked}
                        />
                      ) : (
                        <p className="text-app-text font-medium">{formData.nationality || "N/A"}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-app-text-secondary text-sm">
                        <FaPassport />
                        <span className="font-medium">Visa Status</span>
                      </div>
                      {isEditing ? (
                        <InputField
                          name="visaStatus"
                          value={formData.visaStatus}
                          onChange={handleInputChange}
                          disabled={isPersonalLocked}
                        />
                      ) : (
                        <p className="text-app-text font-medium">{formData.visaStatus || "N/A"}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-app-text-secondary text-sm">
                        <FaCalendarAlt />
                        <span className="font-medium">Visa Expiry</span>
                      </div>
                      {isEditing ? (
                        <InputField
                          name="visaExpiry"
                          value={safeFormat(formData.visaExpiry, "yyyy-MM-dd")}
                          onChange={handleInputChange}
                          type="date"
                          disabled={isPersonalLocked}
                        />
                      ) : (
                        <p className="text-app-text font-medium">
                          {formatDisplayDate(formData.visaExpiry, "N/A")}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-app-text-secondary text-sm">
                        <FaListAlt />
                        <span className="font-medium">Source</span>
                      </div>
                      {isEditing ? (
                        <Autocomplete
                          disabled={isPersonalLocked}
                          options={[
                            { label: "Google", value: "Google" },
                            { label: "Facebook", value: "Facebook" },
                            { label: "Tiktok", value: "Tiktok" },
                            { label: "Instagram", value: "Instagram" },
                            {
                              label: "Family or Friends.",
                              value: "Family or Friends.",
                            },
                            { label: "Other", value: "Other" },
                          ]}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Enter Source"
                              placeholder="Example: Google, Facebook, Tiktok, Instagram, Family or Friends."
                            />
                          )}
                          value={formData.source}
                          size="small"
                          onChange={(e, value) => {
                            handleInputChange({
                              target: { name: "source", value: value?.value },
                            });
                          }}
                          isOptionEqualToValue={(option, value) => option.value === value}
                        />
                      ) : (
                        <p className="text-app-text font-medium">{formData.source || "N/A"}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-app-text-secondary text-sm">
                      <FaListAlt />
                      <span className="font-medium">Interested Services</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {item.interestedServices?.length > 0
                        ? item.interestedServices?.map((service, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                            >
                              {service}
                            </span>
                          ))
                        : "N/A"}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-app-text-secondary text-sm">
                      <FaListAlt />
                      <span className="font-medium">Comments</span>
                    </div>
                    {isEditing ? (
                      <TextArea
                        name="comments"
                        value={formData.comments}
                        onChange={handleInputChange}
                        placeholder="Enter comments..."
                        rows={4}
                        disabled={isPersonalLocked}
                      />
                    ) : (
                      <span className="text-app-text font-medium">
                        {formData.comments || "N/A"}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Follow-up Information Card */}
              <div className="bg-app-surface rounded-lg shadow-xl border border-white/20 backdrop-blur-sm overflow-hidden">
                <div className="bg-secondary px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-app-surface/20 rounded-xl flex items-center justify-center">
                      <FaComments className="text-white text-lg" />
                    </div>
                    <h3 className="text-xl font-semibold text-white">Follow-up Information</h3>
                  </div>
                </div>
                <div className="p-6 space-y-6">
                  {/* Status */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-app-text">
                      Client Conversion Status
                    </label>
                    {isEditing ? (
                      <select
                        name="clientConversionStatus"
                        value={formData.clientConversionStatus}
                        onChange={handleInputChange}
                        disabled={isPersonalLocked}
                        className="w-full px-4 py-3 border border-app-border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-app-surface-muted disabled:cursor-not-allowed"
                      >
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    ) : (
                      <div
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border ${statusConfig?.color}`}
                      >
                        {statusConfig?.icon}
                        <span className="font-medium">{statusConfig?.label}</span>
                      </div>
                    )}
                  </div>

                  {/* Follow-up Date */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-app-text">
                      Follow-up Date
                    </label>
                    {isEditing ? (
                      <InputField
                        type="date"
                        name="followUpDate"
                        value={safeFormat(formData.followUpDate, "yyyy-MM-dd")}
                        onChange={handleInputChange}
                        startIcon={<FaCalendarAlt />}
                      />
                    ) : (
                      <div className="flex items-center gap-2 text-app-text">
                        <FaCalendarAlt className="text-app-text-muted" />
                        <span>{formatDisplayDate(formData.followUpDate, "Not set")}</span>
                      </div>
                    )}
                  </div>

                  {/* Summary of Meeting */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-app-text">
                      Summary of Client Profile
                    </label>
                    {isEditing ? (
                      <TextArea
                        name="summary"
                        value={formData.summary}
                        onChange={handleInputChange}
                        placeholder="Enter meeting summary..."
                        rows={4}
                      />
                    ) : (
                      <div className="p-4 bg-app-surface-muted rounded-xl">
                        <p className="text-app-text">{formData.summary || "No summary recorded"}</p>
                      </div>
                    )}
                  </div>

                  {/* Discussion */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-app-text">
                      Discussion <span className="text-red-500">*</span>
                    </label>
                    {isEditing ? (
                      <TextArea
                        name="discussion"
                        value={formData.discussion}
                        onChange={handleInputChange}
                        placeholder="Enter discussion details..."
                        rows={4}
                        required
                      />
                    ) : (
                      <div className="p-4 bg-app-surface-muted rounded-xl">
                        <p className="text-app-text">
                          {formData.discussion || "No discussion recorded"}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Additional Notes */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-app-text">
                      Additional Notes
                    </label>
                    {isEditing ? (
                      <TextArea
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        placeholder="Enter additional notes..."
                        rows={3}
                      />
                    ) : (
                      <div className="p-4 bg-app-surface-muted rounded-xl">
                        <p className="text-app-text">{formData.notes || "No additional notes"}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Personal & Passport Details */}
            {(formData.dob ||
              formData.maritalStatus ||
              formData.gender ||
              formData.passportNumber ||
              formData.dateOfIssue ||
              formData.passportExpiry ||
              formData.usiNumber ||
              formData.australianAddress ||
              formData.homeCountryAddress ||
              formData.currentCourseProvider ||
              formData.intake ||
              formData.emergencyContactName ||
              formData.emergencyContact ||
              formData.emergencyRelation ||
              formData.emergencyEmail) && (
              <div className="bg-app-surface rounded-lg shadow-xl border border-white/20 backdrop-blur-sm overflow-hidden">
                <div className="bg-indigo-600 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-app-surface/20 rounded-xl flex items-center justify-center">
                      <FaPassport className="text-white text-lg" />
                    </div>
                    <h3 className="text-xl font-semibold text-white">
                      Personal & Passport Details
                    </h3>
                  </div>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[
                      { label: "Date of Birth", value: formData.dob },
                      { label: "Gender", value: formData.gender },
                      { label: "Marital Status", value: formData.maritalStatus },
                      { label: "Passport Number", value: formData.passportNumber },
                      { label: "Date of Issue", value: formData.dateOfIssue },
                      { label: "Passport Expiry", value: formData.passportExpiry },
                      { label: "USI Number", value: formData.usiNumber },
                      { label: "Current Course Provider", value: formData.currentCourseProvider },
                      { label: "Intake", value: formData.intake },
                      { label: "Emergency Contact Name", value: formData.emergencyContactName },
                      { label: "Emergency Relation", value: formData.emergencyRelation },
                      { label: "Emergency Contact", value: formData.emergencyContact },
                      { label: "Emergency Email", value: formData.emergencyEmail },
                    ]
                      .filter((f) => f.value)
                      .map(({ label, value }) => (
                        <div key={label} className="space-y-1">
                          <p className="text-xs font-medium text-app-text-secondary uppercase tracking-wide">
                            {label}
                          </p>
                          <p className="text-app-text font-medium">{value}</p>
                        </div>
                      ))}
                  </div>
                  {(formData.australianAddress || formData.homeCountryAddress) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                      {formData.australianAddress && (
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-app-text-secondary uppercase tracking-wide">
                            Australian Address
                          </p>
                          <p className="text-app-text font-medium whitespace-pre-wrap">
                            {formData.australianAddress}
                          </p>
                        </div>
                      )}
                      {formData.homeCountryAddress && (
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-app-text-secondary uppercase tracking-wide">
                            Home Country Address
                          </p>
                          <p className="text-app-text font-medium whitespace-pre-wrap">
                            {formData.homeCountryAddress}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Additional / Custom Fields */}
            {formData.extraFieldsJson &&
              (() => {
                const METADATA_KEYS = new Set([
                  "id",
                  "createdDate",
                  "updatedDate",
                  "convertedClientId",
                  "extraFieldsJson",
                ]);
                try {
                  const extra = JSON.parse(formData.extraFieldsJson);
                  const entries = Object.entries(extra).filter(
                    ([k, v]) => !METADATA_KEYS.has(k) && v !== null && v !== undefined && v !== ""
                  );
                  if (!entries.length) return null;
                  return (
                    <div className="bg-app-surface rounded-lg shadow-xl border border-white/20 backdrop-blur-sm overflow-hidden">
                      <div className="bg-amber-600 px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-app-surface/20 rounded-xl flex items-center justify-center">
                            <FaListAlt className="text-white text-lg" />
                          </div>
                          <h3 className="text-xl font-semibold text-white">Additional Fields</h3>
                        </div>
                      </div>
                      <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {entries.map(([key, value]) => (
                            <div key={key} className="space-y-1">
                              <p className="text-xs font-medium text-app-text-secondary uppercase tracking-wide">
                                {key.replace(/_/g, " ")}
                              </p>
                              <p className="text-app-text font-medium">
                                {Array.isArray(value) ? value.join(", ") : String(value)}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                } catch {
                  return null;
                }
              })()}
          </div>
        )}
      </div>
      {openEmailModal && (
        <ComposeEmailDrawer
          open={openEmailModal}
          onClose={() => setOpenEmailModal(false)}
          recipient={formData.email}
          message={buildMessage()}
        />
      )}
    </div>
  );
};

export default ViewCheckIn;
